/**
 * A one-time nudge to turn notifications on, shown only inside the installed app.
 *
 * **Scoped to app mode on purpose.** Installing is what makes push possible on iOS at all,
 * so the moment someone opens the installed app is the first moment this ask can succeed
 * there — and it is the moment it makes sense everywhere else too, because they have just
 * told us they want this thing on their home screen. In a browser tab the same nudge would
 * be pestering someone who has expressed no such intent.
 *
 * It pairs with the install nudge rather than competing with it: that one hides once the
 * app is installed, which is exactly when this one appears. The two can never both be up.
 *
 * **The permission prompt is the scarce resource.** Chrome auto-quiets origins with low
 * grant rates and a denial is close to permanent — the reset is several clicks into site
 * settings. So nothing here asks on render; `Enable` runs from a click and nowhere else,
 * and the banner hides entirely once the answer is `denied`, because at that point we
 * cannot re-ask and a button that does nothing is worse than no button.
 */
import { useState } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isStandalone } from '@/features/pwa';
import { useAuth } from '@/hooks/useAuth';
import {
  dismissEnableBanner,
  enableBannerDismissed,
  useEnablePush,
  useLocalPushDevice,
  usePushSupport,
  useVapidKey,
} from '@/hooks/usePush';

export function EnableNotificationsBanner() {
  const { supported, permission } = usePushSupport();
  const { user } = useAuth();
  const { data: vapidKey, isLoading: keyLoading } = useVapidKey();
  // Rendered in the shell on every page, so this is also where the app re-announces this
  // browser as the tab's account on load: it keeps the account's hold bound to its live
  // session, and tells the nudge below whether the account has turned push on here.
  const localDevice = useLocalPushDevice(supported && permission === 'granted');
  const enablePush = useEnablePush();
  const [dismissed, setDismissed] = useState(enableBannerDismissed);

  const dismiss = () => {
    setDismissed(true);
    dismissEnableBanner();
  };

  // Every reason not to ask, in the order they cost nothing to check.
  if (dismissed || !isStandalone() || !supported) return null;
  // `denied` means we are not allowed to ask again, and the notification settings page
  // explains that properly. `granted` means this browser has been through the flow, but
  // push is per account (docs/MultiAccountSessions.md §8): another account signed in here
  // may have turned it on and this one not. One click then, and no browser prompt.
  if (permission === 'denied') return null;
  const otherAccountsOnly = permission === 'granted';
  if (otherAccountsOnly && !(localDevice.isSuccess && localDevice.data === null)) return null;
  // A deployment with no VAPID key configured is a valid state, not a fault — there is
  // simply nothing to enroll against.
  if (keyLoading || !vapidKey) return null;

  return (
    <div className="border-b border-border bg-bg-subtle px-4 py-2.5" role="region" aria-label="Enable notifications">
      <div className="mx-auto flex max-w-5xl items-center gap-3">
        <Bell className="hidden size-4 shrink-0 text-muted-foreground sm:block" aria-hidden />
        <p className="min-w-0 flex-1 text-sm">
          {otherAccountsOnly && user
            ? `Turn on notifications for ${user.email} on this device too.`
            : 'Turn on notifications to hear about incidents while the app is closed.'}
        </p>
        <Button
          size="sm"
          disabled={enablePush.isPending}
          onClick={() => {
            enablePush.mutate(vapidKey, {
              // Whatever the answer, this nudge is done: granted enrolls the browser and
              // the render guard above hides it, and denied means we can never ask again.
              onSettled: dismiss,
            });
          }}
        >
          {enablePush.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Enable
        </Button>
        <Button size="sm" variant="ghost" onClick={dismiss}>
          Not now
        </Button>
      </div>
    </div>
  );
}
